import { Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { StockBalanceList, StockBalanceSummary } from '@aeris/contracts'
import { formatQuantity, parseQuantity, ZERO_QUANTITY } from '@aeris/inventory'
import {
  stockReportFor,
  type PrintDocument,
  type StockReportRow,
} from '@aeris/printing'
import { Button, InlineAlert } from '@aeris/ui'

import { fetchStockBalances } from '../lib/api/inventory.ts'
import type { StockSortKey } from '../lib/stock-columns.tsx'
import {
  stockFilterLabels,
  type StockScreenFilters,
} from '../lib/stock-report-filters.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * O estoque no papel (F150).
 *
 * ## Por que ele busca de novo em vez de imprimir o que está na tela
 *
 * A tela é paginada — 25 linhas. O papel não pode ser: um relatório de estoque
 * com a primeira página da tela é um relatório errado, e quem o lê não tem como
 * saber que faltam duzentos itens.
 *
 * ## E por que não tem mais teto
 *
 * Havia um de mil linhas, e a Elite bateu nele na primeira categoria grande
 * (20/10). A API devolve 100 por vez; o relatório vai buscando até acabar.
 */

const PAGE = 100
/*
 * Sem teto (20/10): a Elite imprimiu as telas, 1.305 peças, e o papel de mil
 * linhas cortou 305 sem dizer quais. O dono: "não pode dar esse erro". O
 * relatório busca todas as páginas do filtro — o estoque inteiro (5 mil
 * peças) são ~50 idas ao servidor, e o botão diz "Montando…" enquanto isso.
 */

/** Os nomes da ordem, para o papel dizer como está ordenado. */
const SORT_LABELS: Readonly<Record<StockSortKey, string>> = {
  name: 'produto',
  sku: 'código',
  brand: 'marca',
  category: 'categoria',
  onHand: 'em mãos',
  reserved: 'reservado',
  available: 'disponível',
  averageCost: 'custo médio',
  salePrice: 'preço de venda',
  margin: 'margem',
  costValue: 'valor em custo',
  saleValue: 'valor em venda',
  updatedAt: 'último movimento',
}

const quantity = (value: string): string =>
  formatQuantity(parseQuantity(value) ?? ZERO_QUANTITY)

const toRow = (balance: StockBalanceSummary): StockReportRow => ({
  description: balance.productName,
  sku: balance.sku,
  onHand: quantity(balance.onHand),
  reserved: quantity(balance.reserved),
  available: quantity(balance.available),
})

export const StockReportButton = ({
  branchId,
  branchName,
  filters,
  sort,
}: {
  branchId: string
  branchName: string
  filters: StockScreenFilters
  /**
   * A ordem da tela (20/10): a Elite ordenou por disponível, do menor para o
   * maior, e o papel saía por nome — a lista de "o que zerou" virava a
   * lista de tudo.
   */
  sort?:
    | { readonly sort: StockSortKey; readonly direction: 'asc' | 'desc' }
    | undefined
}): ReactNode => {
  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  const [document, setDocument] = useState<PrintDocument | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const build = async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const search = filters.search.trim()
      const rows: StockBalanceSummary[] = []
      let total = 0
      /*
       * Os totais vêm do FILTRO, não das linhas que couberam no papel (F163):
       * quando o relatório sai truncado, o rodapé continua dizendo quanto a
       * loja tem, e o aviso logo acima diz que a lista é que está incompleta.
       */
      let totals: StockBalanceList['totals'] | undefined

      for (let page = 1; ; page += 1) {
        const result = await fetchStockBalances({
          ...(search.length > 0
            ? {
                search,
                ...(filters.searchMode === undefined
                  ? {}
                  : { searchMode: filters.searchMode }),
              }
            : {}),
          ...(branchId.length > 0 ? { branchId } : {}),
          onlyWithStock: filters.situacao === 'with',
          negative: filters.situacao === 'negative',
          belowMinimum: filters.situacao === 'below',
          ...(filters.categoryId === undefined || filters.categoryId === ''
            ? {}
            : { categoryId: filters.categoryId }),
          ...(filters.brand === undefined || filters.brand === ''
            ? {}
            : { brand: filters.brand }),
          page,
          pageSize: PAGE,
          ...(sort === undefined
            ? {}
            : { sort: sort.sort, direction: sort.direction }),
        })

        total = result.total
        totals = result.totals
        rows.push(...result.items)
        if (result.items.length < PAGE || rows.length >= total) break
      }

      setDocument(
        stockReportFor({
          company,
          branchName,
          filters: [
            ...stockFilterLabels(filters),
            ...(sort === undefined || sort.sort === 'name'
              ? []
              : [
                  `ordem: ${SORT_LABELS[sort.sort]}, ${sort.direction === 'asc' ? 'menor primeiro' : 'maior primeiro'}`,
                ]),
          ],
          printedAt: new Date(),
          rows: rows.map(toRow),
          totalRows: total,
          totals,
          template,
        })
      )
    } catch {
      // Sem papel e com o motivo dito. Um botão que não faz nada é pior que um
      // que diz que não conseguiu.
      setError(
        'Não foi possível montar o relatório. Tente de novo em alguns instantes.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={loading}
        leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
        onClick={() => {
          void build()
        }}
      >
        {loading ? 'Montando…' : 'Imprimir'}
      </Button>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {document === null ? null : (
        <PrintDocumentDialog
          document={document}
          description="O relatório sai com os filtros que estão na tela."
          initialPaper="a4"
          onClose={() => {
            setDocument(null)
          }}
        />
      )}
    </>
  )
}
